import React, { useEffect, useState, startTransition } from 'react';
import { graphql } from 'gatsby';

import Layout from 'components/common/Layout';
import getHeaderType from 'mui/components/templates/utils/getHeaderType';

import ConnectCfBody from 'utils/connectCfBody';
import getFAQPage from 'utils/getFAQPage';
import prepareDrSmileApiLocations from 'utils/prepareDrSmileApiLocations';
import countries from '../mui/assets/data/countries';

const country = countries[process.env.GATSBY_COUNTRY_CODE];

const legacyCountries = ['pl', 'en-GB'];
const isSwitzerland = country.lang === 'de-CH';

const renderPageComponents = (components, allLocations) => {
  if (components)
    return components.map((component, index) => {
      if (component.__typename === 'ContentfulComponentReviews' && component?.variant?.variant === 'small')
        component.noMinHeight = true;

      if (component.id) {
        const usesLocations = ['ContentfulComponentLocationsList', 'ContentfulComponentDsFindInMap'];

        const sectionMarkup = usesLocations.includes(component.__typename) ? (
          <ConnectCfBody key={`${component.id}_${index}`} {...component} allLocations={allLocations.nodes} />
        ) : (
          <ConnectCfBody key={`${component.id}_${index}`} {...component} />
        );

        // add FAQ structured markup in the page <head>
        if (component.__typename === 'ContentfulComponentDsFaq') {
          if (!component.faqs) return;
          const FAQMarkup = getFAQPage(component.faqs, sectionMarkup, `${component.id}_${index}_FAQMarkup`);

          return FAQMarkup;
        }

        return sectionMarkup;
      }
    });

  return <h2>This page does not have any components in Contentful</h2>;
};

const Page = ({ pageContext, data }) => {
  const { lang, allLangLinks, canonicalLink, slug, selectedDataServiceCode } = pageContext || {};
  const { page, DSHeader, allDrSmileApiLocations, allContentfulLocations } = data || {};
  const [allLocations, setAllLocations] = useState(allContentfulLocations);

  useEffect(() => {
    const fetchLocations = async () => {
      if (pageContext.useLocationsApi) {
        const apiLocations = await prepareDrSmileApiLocations(allDrSmileApiLocations, selectedDataServiceCode);

        startTransition(() => {
          setAllLocations(apiLocations);
        });
      } else {
        startTransition(() => {
          setAllLocations(allContentfulLocations);
        });
      }
    };

    fetchLocations();
  }, [pageContext.useLocationsApi]);

  const setSimpleFooter = legacyCountries.includes(country.lang) || isSwitzerland;
  const legacyCountriesHeader = pageContext.slug === '/' ? 'Yes (disabled homepage link)' : 'Yes';

  const dSHeaderContent = {
    ...DSHeader,
    type: getHeaderType(legacyCountries.includes(country.lang) ? legacyCountriesHeader : page.isSimpleNavigation),
  };

  return (
    <Layout
      lang={lang}
      allLangLinks={allLangLinks}
      canonicalLink={canonicalLink}
      noindex={page?.excludeFromSitemap}
      pathname={slug}
      pageTitle={page?.metaTitle}
      pageDescription={page?.metaDescription?.metaDescription}
      dSHeader={dSHeaderContent}
      image={page?.image}
      redirect={{
        url: page.delayRedirectTo,
        seconds: page.secondsForDelayRedirect,
      }}
      simpleFooter={setSimpleFooter}
    >
      {page?.body ? (
        renderPageComponents(page.body, allLocations)
      ) : (
        <h2>Ups.. Something went wrong with Contentful data</h2>
      )}
    </Layout>
  );
};

export const pageQuery = graphql`
  query pageQuery(
    $id: String!
    $country: String!
    $useLocalFiles: Boolean!
    $locationsApiCountry: String!
    $useLocationsApi: Boolean!
    $selectedDataServiceCode: String!
  ) {
    page: contentfulGlobalPage(id: { eq: $id }) {
      delayRedirectTo
      secondsForDelayRedirect
      isSimpleNavigation
      metaTitle
      metaDescription {
        metaDescription
      }
      excludeFromSitemap
      image {
        file {
          url
        }
      }
      body {
        __typename
        ... on Node {
          ... on ContentfulComponentHero {
            ...Hero
          }
          ... on ContentfulComponentResponsiveImage {
            ...ResponsiveImage
          }
          ... on ContentfulComponentSuccessStories {
            ...SuccessStories
          }
          ... on ContentfulComponentTextWithTwoImages {
            ...TextWithTwoImages
          }
          ... on ContentfulComponentReviews {
            ...Reviews
          }
          ... on ContentfulComponentPriceComparison {
            ...PriceComparison
          }
          ... on ContentfulComponentTextWithBeforeAfter {
            ...TextWithBeforeAfter
          }
          ... on ContentfulComponentInfo {
            ...Info
          }
          ... on ContentfulComponentTextImageRows {
            ...TextImageRows
          }
          ... on ContentfulComponentLocationsList {
            ...LocationsList
          }
          ... on ContentfulComponentCustomContent {
            ...CustomContent
          }
          ... on ContentfulComponentRegisterForm {
            ...RegisterForm
          }
          ... on ContentfulComponentTrustpilot {
            ...Trustpilot
          }
          ... on ContentfulComponentDsPrices {
            ...DS_Prices
          }
          ... on ContentfulComponentDsBasicHero {
            ...DS_BasicHero
          }
          ... on ContentfulComponentDsTrustFacts {
            ...DS_TrustFacts
          }
          ... on ContentfulComponentDsHero {
            ...DS_Hero
          }
          ... on ContentfulComponentDsSteps {
            ...DS_Steps
          }
          ... on ContentfulComponentDsBenefits {
            ...DS_Benefits
          }
          ... on ContentfulComponentDsProblems {
            ...DS_Problems
          }
          ... on ContentfulComponentDsFaq {
            ...DS_Faq
          }
          ... on ContentfulComponentDsTestimonials {
            ...DS_Testimonials
          }
          ... on ContentfulComponentDsFindInMap {
            ...DS_FindInMap
          }
          ... on ContentfulComponentDsPriceHero {
            ...DS_PriceHero
          }
          ... on ContentfulComponentDsHowItWorks {
            ...DS_HowItWorks
          }
          ... on ContentfulComponentDsLocationsCards {
            ...DS_LocationsCards
          }
          ... on ContentfulComponentDsTextWithImage {
            ...DS_TextWithImage
          }
          ... on ContentfulComponentDsAdvantages {
            ...DS_Advantages
          }
          ... on ContentfulComponentDsBanner {
            ...DS_Banner
          }
          ... on ContentfulComponentDsBundles {
            ...DS_Bundles
          }
          ... on ContentfulComponentDsTimeline {
            ...DS_Timeline
          }
          ... on ContentfulComponentDsTimelineSteps {
            ...DS_TimelineSteps
          }
          ... on ContentfulComponentDsTimelineVideo {
            ...DS_TimelineVideo
          }
          ... on ContentfulComponentDsFeatures {
            ...DS_Features
          }
          ... on ContentfulComponentDsTwoImagesAndBenefits {
            ...DS_TwoImgsBenefits
          }
          ... on ContentfulComponentDsCurrentStep {
            ...DS_CurrentStep
          }
          ... on ContentfulComponentDsHeroCard {
            ...DS_HeroCard
          }
          ... on ContentfulComponentDsTwoProductOptions {
            ...DS_TwoProductOptions
          }
          ... on ContentfulComponentDsInfoGraphics {
            ...DS_InfoGraphics
          }
          ... on ContentfulComponentDsInterim {
            ...DS_Interim
          }
          ... on ContentfulComponentDsKnownFrom {
            ...DS_KnownFrom
          }
        }
      }
    }
    allContentfulLocations: allContentfulDataLocation(
      filter: { country: { eq: $country }, code: { ne: null }, image: { file: { url: { ne: null } } } }
      sort: [{ city: ASC }, { code: ASC }]
    ) {
      nodes @skip(if: $useLocationsApi) {
        id
        name
        slug
        code
        country
        state
        city
        location
        heading
        address
        phoneNumber
        rating
        reviews
        reviewsUrl
        flagshipStoreLabel
        openingHours
        openingHoursList {
          markdown: childMarkdownRemark {
            html
          }
        }
        link {
          title
          url
          isButton
          hasBackground
        }
        image {
          gatsbyImageData(width: 330, quality: 100, layout: CONSTRAINED)
          title
        }
        latitude
        longitude
      }
    }
    allDrSmileApiLocations: allLocationsApiDataLocation(
      filter: {
        country: { eq: $locationsApiCountry }
        code: { ne: null }
        image: { url: { ne: null } }
        services: { elemMatch: { code: { eq: $selectedDataServiceCode } } }
      }
      sort: [{ city: ASC }, { code: ASC }]
    ) {
      nodes @include(if: $useLocationsApi) {
        id
        location
        city
        code
        country
        postcode
        state
        heading
        address
        phoneNumber
        image {
          childImageSharp {
            gatsbyImageData(width: 400, layout: CONSTRAINED)
          }
        }
        latitude
        longitude
        hasFreeParking
        flagshipStoreLabel
        services {
          code
          name
          firstAvailableDay
          firstAvailableTime
        }
        marketingAddress
        marketingName
      }
    }
    DSHeader: contentfulGlobalDsHeader(contentful_id: { eq: "63EUi31E4WrT18RnocrXpQ" }) {
      ...GlobalDSHeader
    }
  }
`;

export default Page;
